/**
 * DebtEngine.js - Unified debt calculation module
 * Consolidates simulateSnowball, generatePayoffTimeline, and calculateExtraPaymentForTarget
 * Single source of truth for all debt calculations
 */

/**
 * Core debt calculation class with all methods consolidated
 */
export class DebtEngine {
  constructor(debts, options = {}) {
    // Sort by user-defined order first, then by balance as fallback
    this.debts = JSON.parse(JSON.stringify(debts)).sort((a, b) => {
      const orderA = a.order || 999;
      const orderB = b.order || 999;
      if (orderA !== orderB) return orderA - orderB;
      return a.balance - b.balance; // Fallback to balance if orders are equal
    });
    this.options = {
      maxMonths: 120,
      roundingPrecision: 2,
      ...options
    };
  }

  /**
   * Calculate months to pay off debt using snowball method
   * @param {number} totalPayment - Total monthly payment (minimums + extra)
   * @returns {number} Number of months to pay off debt (or -1 if > maxMonths)
   */
  calculatePayoffMonths(totalPayment) {
    const debts = JSON.parse(JSON.stringify(this.debts));
    const totalDebt = debts.reduce((sum, debt) => sum + debt.balance, 0);

    // Edge case: no debts
    if (totalDebt === 0) return 0;

    // Edge case: total payment clears all debts in 1 month
    if (totalPayment >= totalDebt) return 1;

    for (let month = 1; month <= this.options.maxMonths; month++) {
      let available = totalPayment;

      // Step 1: Apply interest and pay minimum payments
      for (let debt of debts) {
        if (debt.balance <= 0) continue;

        // Calculate and apply interest first
        const interest = this._roundToPrecision(debt.balance * (debt.rate / 12 / 100));
        debt.balance += interest;

        // Apply full minimum payment to balance
        const actualPayment = Math.min(debt.balance, debt.minPayment);
        debt.balance = Math.max(0, debt.balance - actualPayment);
        
        // Deduct actual payment from available funds
        available -= debt.minPayment;
      }

      // Ensure available payment is never negative
      available = Math.max(0, available);

      // Step 2: Apply extra payment to smallest remaining debt
      if (available > 0) {
        for (let debt of debts) {
          if (debt.balance > 0) {
            const extraPayment = Math.min(available, debt.balance);
            debt.balance = Math.max(0, debt.balance - extraPayment);
            break; // Only apply extra to one debt
          }
        }
      }

      // Step 3: Check if all debts are cleared
      const totalRemaining = debts.reduce((sum, debt) => sum + debt.balance, 0);
      if (totalRemaining <= 1) return month; // Allow for small rounding errors
    }

    return -1; // Not paid off within maxMonths
  }

  /**
   * Generate detailed month-by-month payoff timeline
   * @param {number} totalPayment - Total monthly payment
   * @returns {Array} Timeline entries with monthly breakdown
   */
  generateTimeline(totalPayment) {
    const debts = JSON.parse(JSON.stringify(this.debts));
    const totalDebt = debts.reduce((sum, d) => sum + d.balance, 0);
    const timeline = [];

    if (totalPayment >= totalDebt) {
      // Immediate payoff
      timeline.push({
        month: 1,
        totalDebt: 0,
        interestPaid: 0,
        principalPaid: totalDebt,
        remainingDebts: debts.map(d => ({ name: d.name, balance: 0 })),
      });
      return timeline;
    }

    for (let month = 1; month <= this.options.maxMonths; month++) {
      let available = totalPayment;
      let totalInterestPaid = 0;
      let totalPrincipalPaid = 0;

      // Step 1: Apply interest + minimum payments
      for (let debt of debts) {
        if (debt.balance <= 0) continue;

        const interest = this._roundToPrecision(debt.balance * (debt.rate / 12 / 100));
        debt.balance += interest;
        totalInterestPaid += interest;

        const minPayment = Math.min(debt.balance, debt.minPayment);
        debt.balance = Math.max(0, debt.balance - minPayment);
        totalPrincipalPaid += Math.max(minPayment - interest, 0);

        available -= minPayment;
      }

      available = Math.max(0, available);

      // Step 2: Apply extra to smallest debt
      if (available > 0) {
        for (let debt of debts) {
          if (debt.balance > 0) {
            const extraPayment = Math.min(available, debt.balance);
            debt.balance = Math.max(0, debt.balance - extraPayment);
            totalPrincipalPaid += extraPayment;
            break;
          }
        }
      }

      const totalRemaining = debts.reduce((sum, d) => sum + d.balance, 0);

      timeline.push({
        month,
        totalDebt: this._roundToPrecision(totalRemaining),
        interestPaid: this._roundToPrecision(totalInterestPaid),
        principalPaid: this._roundToPrecision(totalPrincipalPaid),
        remainingDebts: debts.map(d => ({ 
          name: d.name, 
          balance: this._roundToPrecision(d.balance) 
        })),
      });

      if (totalRemaining <= 1) break; // All debts cleared
    }

    return timeline;
  }

  /**
   * Calculate required extra payment to reach target months using binary search
   * @param {number} targetMonths - Desired payoff timeline
   * @param {number} totalMinPayments - Sum of all minimum payments
   * @returns {number} Required extra payment amount
   */
  calculateExtraForTarget(targetMonths, totalMinPayments) {
    if (targetMonths <= 0) return 0;
    
    // Use realistic upper bound based on total debt
    const totalDebt = this.debts.reduce((sum, debt) => sum + debt.balance, 0);
    
    let low = 0;
    let high = totalDebt;
    let bestExtra = 0;

    while (low <= high) {
      const midExtra = Math.floor((low + high) / 2);
      const months = this.calculatePayoffMonths(totalMinPayments + midExtra);

      if (months > 0 && months <= targetMonths) {
        bestExtra = midExtra;
        high = midExtra - 1; // Try to find smaller payment
      } else {
        low = midExtra + 1; // Need higher payment
      }
    }

    return bestExtra;
  }

  /**
   * Generate comparison scenarios (Do Nothing, Minimum Only, Snowball)
   * @param {number} extraPayment - Extra payment amount for snowball
   * @returns {Object} Scenario data for charts
   */
  generateScenarios(extraPayment = 0) {
    const totalMinPayments = this.debts.reduce((sum, debt) => sum + debt.minPayment, 0);
    
    return {
      doNothing: this._generateDoNothingScenario(),
      minimumOnly: this._generateMinimumOnlyScenario(),
      snowball: this.generateTimeline(totalMinPayments + extraPayment)
    };
  }

  /**
   * Get key metrics summary
   * @param {number} extraPayment - Extra payment amount
   * @returns {Object} Key metrics for dashboard
   */
  getMetrics(extraPayment = 0) {
    const totalMinPayments = this.debts.reduce((sum, debt) => sum + debt.minPayment, 0);
    const totalDebt = this.debts.reduce((sum, debt) => sum + debt.balance, 0);
    
    const snowballMonths = this.calculatePayoffMonths(totalMinPayments + extraPayment);
    const minimumMonths = this.calculatePayoffMonths(totalMinPayments);
    
    const snowballTimeline = this.generateTimeline(totalMinPayments + extraPayment);
    const minimumTimeline = this.generateTimeline(totalMinPayments);
    
    const snowballInterest = snowballTimeline.reduce((sum, entry) => sum + entry.interestPaid, 0);
    const minimumInterest = minimumTimeline.reduce((sum, entry) => sum + entry.interestPaid, 0);
    
    return {
      totalDebt,
      totalMinPayments,
      snowballPayment: totalMinPayments + extraPayment,
      snowballMonths,
      minimumMonths,
      monthsSaved: Math.max(0, minimumMonths - snowballMonths),
      interestSaved: Math.max(0, minimumInterest - snowballInterest),
      isImmediatePayoff: snowballMonths === 1
    };
  }

  // Private helper methods
  _roundToPrecision(value) {
    const factor = Math.pow(10, this.options.roundingPrecision);
    return Math.round((value + Number.EPSILON) * factor) / factor;
  }

  _generateDoNothingScenario() {
    const data = [];
    for (let month = 0; month <= 60; month++) {
      const total = this.debts.reduce((acc, debt) => {
        const monthlyRate = debt.rate / 12 / 100;
        const futureBalance = debt.balance * Math.pow(1 + monthlyRate, month);
        return acc + futureBalance;
      }, 0);
      
      data.push({ 
        month, 
        balance: this._roundToPrecision(total),
        totalDebt: this._roundToPrecision(total)
      });
    }
    return data;
  }

  _generateMinimumOnlyScenario() {
    return this.generateTimeline(this.debts.reduce((sum, debt) => sum + debt.minPayment, 0));
  }
}

// Backwards compatibility exports
export const simulateSnowball = (debts, totalPayment) => {
  const engine = new DebtEngine(debts);
  return engine.calculatePayoffMonths(totalPayment);
};

export const generatePayoffTimeline = (debts, totalPayment) => {
  const engine = new DebtEngine(debts);
  return engine.generateTimeline(totalPayment);
};

export const calculateExtraPaymentForTarget = (targetMonths, debts, totalMinPayments) => {
  const engine = new DebtEngine(debts);
  return engine.calculateExtraForTarget(targetMonths, totalMinPayments);
};